import { HermesOtlStoreModule } from '../../account/store/hermes-otl.module';
import isProduction from './is-production';
import isRTL from './is-lang-arabic';

interface RedirectToHermesOptions {
  storeId: number | string;
  purchaseId?: string | null;
  packageCode?: string | null;
  appId?: string | null;
  redirectToWallet?: boolean;
  billing?: boolean; // If true, redirects to finance/billing (not finance/billing/packages)
  /** When true, OTL URL includes addons-checkout=1 (add-on purchasables checkout, not core package only). */
  addonsCheckout?: boolean;
  /** Purchasable / add-on IDs, appended as repeated addons[]=… (same shape as subscription checkout query). */
  addonIds?: string[] | null;
  /** Contact phone for services that require it; sent as query param `contact-phone` on the OTL URL. */
  contactPhone?: string | null;
  posQuantity?: number | null;
  posCashierId?: string | null;
  pos?: boolean;
}

/**
 * Redirects to Hermes with OTL token authentication
 * Uses the /login/otl/{token} route to authenticate the user into the same store
 * Passes redirect flags and optional parameters:
 * - store-id: Store ID for the target store
 * - redirectToWallet: If true, passes only redirect_to_wallet=1 (redirects to /finance/wallet/funds)
 * - billing: If true, passes only billing=1 (redirects to finance/billing)
 * - redirect_to_subscription=1: For subscription/packages pages (default, redirects to finance/billing/packages)
 * - app-id: For app checkout (redirects to /checkout with filters.addons)
 * - package-code: For package subscriptions (redirects with filters.package_code)
 * - purchase-id: For existing purchases (redirects with purchase_id)
 * - addons-checkout=1: Add-on / SMS / extra services checkout (not package_code-only flow)
 * - addons[] (repeated): purchasable IDs when addonIds is set (encoded as addons%5B%5D=…)
 * - purchasable-ids: same IDs comma-separated (easier for Hermes to read in one param)
 * - contact-phone: optional phone (same name as /subscription/checkout?contact-phone=…)
 * Hermes will authenticate using the OTL token and redirect to the appropriate page based on the parameters
 */
/** @returns true if navigation was triggered (Hermes or /home on error). false if OTL token missing. */
export default async function redirectToHermes(options: RedirectToHermesOptions): Promise<boolean> {
  try {
    const { token } = await HermesOtlStoreModule.getHermesOtl();
    const lang = isRTL() ? 'ar-sa' : 'en-sa';
    const baseUrl = isProduction() ? 'https://dashboard.zid.sa' : 'https://hermes.zid.dev';

    if (!token) return false;

    // Build query parameters for redirect information
    const params = new URLSearchParams();

    // Set redirect flag - only one flag should be set
    if (options.redirectToWallet) {
      params.set('redirect_to_wallet', '1');
    } else if (options.billing) {
      // Billing page - only set billing parameter
      params.set('billing', '1');
    } else {
      // Subscription/packages page - only set redirect_to_subscription
      params.set('redirect_to_subscription', '1');
    }

    // Add store ID
    if (options.storeId) {
      params.set('store-id', String(options.storeId));
    }

    // Add app-id if provided (for app checkout)
    if (options.appId && String(options.appId).trim() !== '') {
      params.set('app-id', String(options.appId).trim());
    }

    // Add POS-specific parameters for cashier checkout
    if (options.pos) {
      params.set('pos', '1');
    }

    if (options.posQuantity && options.posQuantity > 0) {
      params.set('filters.quantity', String(options.posQuantity));
    }

    if (options.posCashierId && String(options.posCashierId).trim() !== '') {
      params.set('filters.pos_cashier_id', String(options.posCashierId).trim());
    }

    // Add package-code if provided (for package subscriptions)
    if (options.packageCode && String(options.packageCode).trim() !== '') {
      params.set('package-code', String(options.packageCode).trim());
    }

    // Add purchase-id if provided (for existing purchases)
    if (options.purchaseId && String(options.purchaseId).trim() !== '') {
      params.set('purchase-id', String(options.purchaseId).trim());
    }

    if (options.addonsCheckout) {
      params.set('addons-checkout', '1');
    }

    if (options.addonIds?.length) {
      const uniqueIds = Array.from(new Set(options.addonIds.map((id) => String(id).trim()).filter(Boolean)));
      for (const id of uniqueIds) {
        params.append('addons[]', id);
      }
      if (uniqueIds.length) {
        params.set('purchasable-ids', uniqueIds.join(','));
      }
    }

    if (options.contactPhone && String(options.contactPhone).trim() !== '') {
      params.set('contact-phone', String(options.contactPhone).trim());
    }

    // Use the /login/otl/{token} route pattern with redirect parameters
    const loginPath = `/${lang}/login/otl/${token}?${params.toString()}`;
    const loginUrl = `${baseUrl}${loginPath}`;

    // Redirect to Hermes login which will authenticate using OTL and redirect to finance/billing
    window.location.href = loginUrl;
    return true;
  } catch (error) {
    console.error('Failed to redirect to Hermes:', error);
    // Fallback: redirect to home or show error
    window.location.href = '/home';
    return true;
  }
}
